import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
export default function Signup() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ name: "", email: "", phone:"", password: "" });
    const [loading, setLoading] = useState(false);
    const [focused, setFocused] = useState("");
    const [strength, setStrength] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [locationStatus, setLocationStatus] = useState("idle"); // idle | detecting | granted | denied
    const [manualCity, setManualCity] = useState("");
    const [locationData, setLocationData] = useState({ lat: null, lng: null, city: null });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (name === "password") {
            let s = 0;
            if (value.length >= 6) s++;
            if (/[A-Z]/.test(value)) s++;
            if (/[0-9]/.test(value)) s++;
            if (/[^A-Za-z0-9]/.test(value)) s++;
            setStrength(s);
        }
    };
    // Add this function inside Signup component
    // Auto detect via GPS
    const detectLocation = () => {
        setLocationStatus("detecting");

        if (!navigator.geolocation) {
            setLocationStatus("denied");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                try {
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
                    );
                    const data = await res.json();
                    const addr = data.address;

                    // ✅ Priority order — most specific to least specific
                    const city =
                        addr.city ||           // big cities like Hyderabad, Mumbai
                        addr.town ||           // medium towns
                        addr.municipality ||   // municipal areas
                        addr.district ||       // district level
                        addr.county ||         // county
                        addr.state_district || // state district
                        addr.village ||        // small villages
                        addr.state ||          // fallback to state
                        null;

                    console.log("Full address from Nominatim:", addr); // 👈 check this in console

                    setLocationData({ lat, lng, city });
                    setLocationStatus("granted");
                } catch {
                    setLocationData({ lat, lng, city: null });
                    setLocationStatus("granted");
                }
            },
            (err) => {
                console.error("Geolocation error:", err);
                setLocationStatus("denied");
            }
        );
    };
    // Convert manual city name → lat/lng
    const applyManualCity = async () => {
        if (!manualCity.trim()) return;
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(manualCity)}&format=json&limit=1`
            );
            const data = await res.json();
            if (data.length > 0) {
                setLocationData({
                    lat: parseFloat(data[0].lat),
                    lng: parseFloat(data[0].lon),
                    city: manualCity,
                });
            } else {
                setLocationData({ lat: null, lng: null, city: manualCity });
            }
        } catch {
            setLocationData({ lat: null, lng: null, city: manualCity });
        }
    };
    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);

        const res = await fetch(`${API}/api/auth/signup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                ...formData,
                latitude: locationData.lat,
                longitude: locationData.lng,
                city: locationData.city,
            }),
        });
        const data = await res.json();
        setLoading(false);
        if (res.ok) {
            toast.success(data.message);
            setTimeout(() => navigate("/login"), 2000);
        } else {
            toast.error(data.message);
        }
    };

    const strengthColors = ["#dde6ef", "#e74c3c", "#f39c12", "#27ae60", "#1a6fa8"];
    const strengthLabels = ["", "Weak", "Fair", "Good", "Strong"];

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,600&family=DM+Sans:wght@300;400;500&display=swap');
        .font-cormorant { font-family: 'Cormorant Garamond', serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner {
          display: inline-block; width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
          border-radius: 50%; animation: spin 0.7s linear infinite;
          vertical-align: middle; margin-right: 8px;
        }
        .teal-input:focus { box-shadow: 0 0 0 3px rgba(13,143,143,0.1); }
        .strength-seg { transition: background 0.3s; }
      `}</style>

            <div className="font-dm min-h-screen flex">

                {/* Left Panel */}
                <div
                    className="hidden md:flex w-[45%] flex-col justify-between p-12 relative overflow-hidden"
                    style={{ background: "linear-gradient(145deg,#0d6e6e 0%,#148080 50%,#0a3d62 100%)" }}
                >
                    <div className="absolute w-[500px] h-[500px] rounded-full border border-white/[0.07] -top-36 -left-36 pointer-events-none" />
                    <div className="absolute w-[300px] h-[300px] rounded-full bg-white/[0.03] -bottom-16 -right-16 pointer-events-none" />

                    {/* Brand */}
                    <div className="flex items-center gap-3 z-10">
                        <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center text-2xl">🏥</div>
                        <span className="font-cormorant text-[22px] font-semibold text-white tracking-wide">MediBook</span>
                    </div>

                    {/* Headline */}
                    <div className="z-10">
                        <h1 className="font-cormorant text-[44px] font-light text-white leading-[1.2] mb-5">
                            Start your<br />
                            <span className="font-semibold italic">wellness</span><br />
                            journey.
                        </h1>
                        <p className="text-white/62 text-[15px] leading-relaxed font-light max-w-[300px]">
                            Join thousands of patients who trust MediBook for hassle-free healthcare appointments.
                        </p>

                        {/* Features list */}
                        <ul className="mt-9 space-y-3.5">
                            {[
                                ["📅", "Instant appointment booking"],
                                ["👨‍⚕️", "500+ verified specialists"],
                                ["🔔", "Smart reminders & follow-ups"],
                                ["💬", "In-app chat with doctors"],
                            ].map(([icon, text]) => (
                                <li key={text} className="flex items-center gap-2.5 text-white/80 text-sm font-light">
                                    <div className="w-7 h-7 bg-white/[0.12] rounded-lg flex items-center justify-center text-[13px] shrink-0">
                                        {icon}
                                    </div>
                                    {text}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Decorative cross */}
                    <svg className="absolute top-44 right-9 z-10 opacity-[0.10]" width="100" height="100" viewBox="0 0 100 100" fill="none">
                        <rect x="38" y="0" width="24" height="100" rx="12" fill="white" />
                        <rect x="0" y="38" width="100" height="24" rx="12" fill="white" />
                    </svg>

                    <p className="z-10 text-white/35 text-xs tracking-wide">© 2025 MediBook · All rights reserved</p>
                </div>

                {/* Right Panel */}
                <div className="flex-1 flex items-center justify-center px-8 py-12 bg-slate-50 overflow-y-auto">
                    <div className="w-full max-w-sm">
                        <h2 className="font-cormorant text-[36px] font-medium text-[#0a3d62] mb-1">Create account</h2>
                        <p className="text-slate-400 text-sm font-light mb-8">Free to join · No credit card required</p>

                        <form onSubmit={handleSignup}>
                            {/* Full Name */}
                            <div className="mb-[18px]">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                                    Full Name
                                </label>
                                <div className="relative">
                                    <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-[15px] pointer-events-none transition-colors duration-200 ${focused === "name" ? "text-[#0d8f8f]" : "text-slate-300"}`}>
                                        👤
                                    </span>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="John Doe"
                                        value={formData.name}
                                        onChange={handleChange}
                                        onFocus={() => setFocused("name")}
                                        onBlur={() => setFocused("")}
                                        required
                                        className="teal-input font-dm w-full pl-10 pr-4 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#0d8f8f]"
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className="mb-[18px]">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-[15px] pointer-events-none transition-colors duration-200 ${focused === "email" ? "text-[#0d8f8f]" : "text-slate-300"}`}>
                                        ✉
                                    </span>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="you@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onFocus={() => setFocused("email")}
                                        onBlur={() => setFocused("")}
                                        required
                                        className="teal-input font-dm w-full pl-10 pr-4 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#0d8f8f]"
                                    />
                                </div>
                            </div>
                            {/* Phone Number */}
<div className="mb-[18px]">
    <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
        Phone Number
    </label>

    <div className="relative">
        <span
            className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-[15px] pointer-events-none transition-colors duration-200 ${
                focused === "phone"
                    ? "text-[#0d8f8f]"
                    : "text-slate-300"
            }`}
        >
            📱
        </span>

        <input
            type="tel"
            name="phone"
            placeholder="9876543210"
            value={formData.phone}
            onChange={handleChange}
            onFocus={() => setFocused("phone")}
            onBlur={() => setFocused("")}
            required
            pattern="[6-9][0-9]{9}"
            maxLength={10}
            className="teal-input font-dm w-full pl-10 pr-4 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#0d8f8f]"
        />
    </div>
</div>

                            {/* Password */}
                            <div className="mb-3">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                                    Password
                                </label>

                                <div className="relative">
                                    <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-base pointer-events-none ${focused === "password" ? "text-[#1a6fa8]" : "text-slate-300"
                                        }`}>
                                        🔒
                                    </span>

                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder="Enter your password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        onFocus={() => setFocused("password")}
                                        onBlur={() => setFocused("")}
                                        required
                                        className="auth-input font-dm w-full pl-10 pr-10 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none focus:border-[#1a6fa8]"
                                    />

                                    {/* Eye Icon */}
                                    <span
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer text-lg text-slate-400 hover:text-[#1a6fa8]"
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </div>
                            </div>

                            {/* Location */}
                            <div className="mb-[18px]">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                                    Your Location
                                </label>

                                {/* idle — show detect button */}
                                {locationStatus === "idle" && (
                                    <button
                                        type="button"
                                        onClick={detectLocation}
                                        className="w-full py-3 border-[1.5px] border-dashed border-slate-300 rounded-xl text-sm text-slate-500 hover:border-[#0d8f8f] hover:text-[#0d8f8f] transition-all duration-200 flex items-center justify-center gap-2"
                                    >
                                        📍 Detect my location
                                    </button>
                                )}

                                {/* detecting — loading state */}
                                {locationStatus === "detecting" && (
                                    <div className="w-full py-3 border-[1.5px] border-slate-200 rounded-xl text-sm text-slate-400 flex items-center justify-center gap-2">
                                        <span className="spinner" /> Detecting...
                                    </div>
                                )}

                                {/* granted — show detected city */}
                                {locationStatus === "granted" && (
                                    <div className="w-full py-3 px-4 border-[1.5px] border-green-300 bg-green-50 rounded-xl text-sm text-green-700 flex items-center justify-between">
                                        <span>📍 {locationData.city || "Location detected"}</span>
                                        <button
                                            type="button"
                                            onClick={() => setLocationStatus("idle")}
                                            className="text-xs text-slate-400 hover:text-red-400"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                )}

                                {/* denied — show manual city input */}
                                {locationStatus === "denied" && (
                                    <div className="flex flex-col gap-2">
                                        <p className="text-xs text-slate-400">
                                            Location access denied.{" "}
                                            <button
                                                type="button"
                                                onClick={detectLocation}
                                                className="text-[#0d8f8f] hover:underline"
                                            >
                                                Try again
                                            </button>
                                        </p>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                placeholder="Enter your city (e.g. Hyderabad)"
                                                value={manualCity}
                                                onChange={(e) => setManualCity(e.target.value)}
                                                className="teal-input font-dm flex-1 px-4 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none focus:border-[#0d8f8f]"
                                            />
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    await applyManualCity();
                                                    setLocationStatus("granted");
                                                }}
                                                className="px-4 py-3 rounded-xl text-white text-sm"
                                                style={{ background: "linear-gradient(135deg,#0d6e6e,#0a3d62)" }}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Terms */}
                            <p className="text-[13px] text-slate-400 leading-relaxed mb-6">
                                By creating an account, you agree to our{" "}
                                <a href="#" className="text-[#0d8f8f] hover:underline">Terms of Service</a>{" "}
                                and{" "}
                                <a href="#" className="text-[#0d8f8f] hover:underline">Privacy Policy</a>.
                            </p>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="font-dm w-full py-3.5 rounded-xl text-white text-[15px] font-medium tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:!transform-none"
                                style={{ background: "linear-gradient(135deg,#0d6e6e,#0a3d62)" }}
                            >
                                {loading && <span className="spinner" />}
                                {loading ? "Creating account..." : "Create Account"}
                            </button>
                        </form>

                        {/* Divider */}
                        <div className="flex items-center gap-3 my-6 text-slate-300 text-xs">
                            <div className="flex-1 h-px bg-slate-200" />
                            already have an account?
                            <div className="flex-1 h-px bg-slate-200" />
                        </div>

                        <p className="text-center text-sm">
                            <Link to="/login" className="text-[#0d8f8f] font-medium hover:underline">
                                Sign in instead →
                            </Link>
                        </p>
                    </div>
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={2000} />
        </>
    );
}